import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  updateProfile,
  type AuthProvider,
} from 'firebase/auth';
import { useState } from 'react';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, firebaseConfigured } from '../../lib/firebase';
import { isNative } from '../../lib/native';
import { friendlyAuthError } from './authErrors';
import { useT } from '../../i18n';
import { TERMS_VERSION } from '@lineage/core';
import { api } from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { keys } from '../../lib/queries';
import { AgreeCheckbox, ContinuingNotice } from '../../components/Legal';

const isMobileBrowser = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

type Mode = 'signin' | 'create' | 'reset';

export function SignInPanel() {
  const t = useT().auth;
  const tl = useT().legal;
  const [agree, setAgree] = useState(false);
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setError('');
    setInfo('');
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const social = (provider: AuthProvider) =>
    run(async () => {
      // Native sheet in the apps (Google blocks WebView OAuth). The plugin signs in the native SDK only, so hand
      // its Google ID token to the JS SDK, which is what the app and API calls use.
      if (isNative) {
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) throw Object.assign(new Error('Google sign-in returned no ID token'), { code: 'lineage/no-id-token' });
        await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
        return;
      }
      try {
        // Popups are unreliable in mobile browsers, so fall back to a full-page redirect there.
        if (isMobileBrowser()) await signInWithRedirect(auth, provider);
        else await signInWithPopup(auth, provider);
      } catch (e) {
        if ((e as { code?: string }).code === 'auth/popup-blocked') await signInWithRedirect(auth, provider);
        else throw e;
      }
    });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (mode === 'signin') await signInWithEmailAndPassword(auth, email.trim(), password);
      else if (mode === 'create') {
        if (!agree) {
          setError(tl.mustAgree);
          return;
        }
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        // The box was ticked on this screen, so record it now and skip the agreement screen.
        await api('PATCH', '/me', { acceptTerms: TERMS_VERSION }).then(() => qc.invalidateQueries({ queryKey: keys.me }), () => {});
        if (name.trim()) await updateProfile(cred.user, { displayName: name.trim() });
        await sendEmailVerification(cred.user);
      } else {
        await sendPasswordResetEmail(auth, email.trim());
        // Same message whether or not the address has an account (email enumeration protection).
        setInfo(t.resetSent);
      }
    });
  };

  return (
    <div className="stack" style={{ textAlign: 'left' }}>
      {!firebaseConfigured && (
        <div className="banner" role="alert">
          Sign-in isn’t configured yet. Add the Firebase settings from <code>apps/web/.env.example</code>.
        </div>
      )}
      <button className="btn btn-block" type="button" disabled={busy} onClick={() => social(new GoogleAuthProvider())}>
        {t.continueWithGoogle}
      </button>
      <div className="muted" style={{ textAlign: 'center', fontSize: 13 }}>{t.orUseEmail}</div>
      <form className="stack" onSubmit={submit} noValidate>
        {mode === 'create' && (
          <label className="field">
            <span>{t.yourName}</span>
            <input className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          </label>
        )}
        <label className="field">
          <span>{t.email}</span>
          <input className="input" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        {mode !== 'reset' && (
          <label className="field">
            <span>{t.password}</span>
            <input
              className="input"
              type="password"
              autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
        )}
        {mode === 'create' && <AgreeCheckbox checked={agree} onChange={setAgree} />}
        {error && <div className="error" role="alert">{error}</div>}
        {info && <div className="banner" role="status">{info}</div>}
        <button className="btn btn-primary btn-block" type="submit" disabled={busy || !email || (mode !== 'reset' && !password) || (mode === 'create' && !agree)}>
          {mode === 'signin' ? t.signIn : mode === 'create' ? t.createAccount : t.sendResetLink}
        </button>
      </form>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        {mode === 'signin' ? (
          <>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => setMode('reset')}>{t.forgotPassword}</button>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => setMode('create')}>{t.createAccount}</button>
          </>
        ) : (
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => setMode('signin')}>{t.backToSignIn}</button>
        )}
      </div>
      {mode !== 'create' && <ContinuingNotice />}
    </div>
  );
}
